import { Directive, Host, Input, OnDestroy, OnInit, Optional, Output, EventEmitter } from '@angular/core';
import { QuillEditorComponent } from 'ngx-quill';
import Quill, { Delta, EmitterSource } from 'quill';
import { SubSink } from 'subsink';

import { QuillTemplate } from '../../generated/graphql.generated';
import { TemplateToolbarQuillModule } from '../jobsv2/job-activity/quill-modules/templateToolbar.quillModule';
import { VariableToolbarQuillModule } from '../jobsv2/job-activity/quill-modules/variableToolbar.quillModule';

interface OnDefaultTemplateSetEvent {
    source: EmitterSource;
    content: Delta
    text: string;
}
@Directive({
  selector: '[appQuillTemplateVariableToolbar]',
  standalone: true,
})
export class QuillTemplateVariableToolbarDirective implements OnInit, OnDestroy {
  @Input() templatesAndVariables$: any;
  @Input() templateVariablesData$: any;
  @Input() editorId?: string;
  @Input() setDefaultTemplate: boolean = false;
  @Output() defaultTemplateSetCallback?: (event: OnDefaultTemplateSetEvent) => void;
  @Output() templateSelected = new EventEmitter<QuillTemplate>();
  @Input() isSubject?: boolean = false;

  private subs = new SubSink();
  private defaultTemplateSet = false;

  constructor(
    @Host() @Optional() private quillEditorComponent: QuillEditorComponent
  ) {}

  private setDefaultTemplateIfNeeded(quill: Quill, templates: QuillTemplate[]) {
    if(this.defaultTemplateSet) return;

    const defaultTemplateAccessor = this.isSubject ? 'subjectContents' : 'contents';
    const editorContents = quill.getText().trim();
    const isEditorEmpty = !(editorContents.length > 0);
    if (!this.setDefaultTemplate || !isEditorEmpty) return;

    const defaultTemplate = templates.find((t: any) => t.isDefault);
    if (
      this.setDefaultTemplate &&
      defaultTemplate?.[defaultTemplateAccessor] &&
      templates &&
      templates.length > 0 &&
      isEditorEmpty
    ) {
      try {
        const delta = JSON.parse(defaultTemplate[defaultTemplateAccessor]);
        quill.setContents(delta, 'user');
        this.defaultTemplateSet = true;
        if (this.defaultTemplateSetCallback) {
          const templateEvent: OnDefaultTemplateSetEvent = {
            source: 'silent',
            content: delta,
            text: quill.getText(),
          };
          this.defaultTemplateSetCallback(templateEvent);
        }
      } catch (e) {
          console.error('Error setting default template:', e);
        }
    }
  }

  ngOnInit() {
    if (this.quillEditorComponent) {
      this.subs.sink = this.quillEditorComponent.onEditorCreated.subscribe((quill: Quill) => {
        // Log the editor identifier
        console.log('Quill editor created:', this.editorId || 'unknown');

        // Subscribe to templates and variables
        this.subs.sink = this.templatesAndVariables$.subscribe(({ templates, variables }) => {
          const templateToolbar = quill.getModule('templateToolbar') as TemplateToolbarQuillModule;
          const variableToolbar = quill.getModule('variableToolbar') as VariableToolbarQuillModule;
          if (templateToolbar) {
            templateToolbar.updateTemplates(templates);
            templateToolbar.onTemplateSelected = (template) => {
              this.templateSelected.emit(template);
            };
          }
          if (variableToolbar) variableToolbar.updateVariables(variables);

          this.setDefaultTemplateIfNeeded(quill, templates);
        });

        // Subscribe to template variables data
        this.subs.sink = this.templateVariablesData$.subscribe((templateVariablesData) => {
          const variableToolbar = quill.getModule('variableToolbar') as VariableToolbarQuillModule;
          if (variableToolbar) variableToolbar.updateData(templateVariablesData);
        });
      });
    }
  }

  ngOnDestroy() {
    console.log('Destroying Quill editor:', this.editorId || 'unknown');

    this.subs.unsubscribe();
  }
}
